import { Checkbox } from '#app/components/Controls/src/Checkbox.js';
import { DialogSelect } from '#app/components/DialogSelect/DialogSelect.js';
import { useDialogSelect } from '#app/components/DialogSelect/hooks/useDialogSelect.js';
import { DialogSelectProps } from '#app/components/DialogSelect/types.js';
import { Menu } from '#app/components/Menu/Menu.js';
import { Text } from '#app/components/Primitives/Text.js';
import { TextInput } from '#app/components/TextInput/TextInput.js';
import { Tooltip } from '#app/components/Tooltip/Tooltip.js';
import { useAnalytics } from '#app/hooks/useAnalytics.js';
import { useIsSmallScreen } from '#app/hooks/useIsSmallScreen.js';
import { VerifiedExpertListBadge } from '../VerifiedExpertListBadge.js';
import { AddIcon, CloseIcon, MoreIcon, TrashIcon } from '@contra/icons/icons';
import { useClickAway } from '@contra/react-hooks/useClickAway';
import {
  ChangeEvent,
  KeyboardEvent,
  MouseEventHandler,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import { css, styled } from 'styled-components';

type AddToListMenuSelectProps = DialogSelectProps & {
  readonly analyticsSection: string;
  readonly availableLists: Item[];
  readonly disableSearchAndCreateInput?: boolean;
  readonly isOpen: boolean;
  readonly isParentMenuOpen: boolean;
  readonly onClose: () => void;
  readonly onListDeleted: (listId: string) => void;
  readonly onListNameUpdated: (listId: string, newName: string) => void;
  readonly onNewListCreated: (newListName: string) => void;
  readonly onSelectedItemChange: (lists: Item[]) => void;
  readonly parentMenuRef?: React.RefObject<HTMLDivElement>;
  readonly selectedLists: Item[];
  readonly warningMessage?: string;
};

type Item = {
  id: string;
  isExpertList: boolean;
  name: string;
};

const CheckboxContainer = styled.div`
  margin-top: 4px;
  margin-bottom: auto;
`;

const ClearSearchButton = styled.button`
  position: absolute;
  right: 16px;
  top: 16px;
  height: 24px;
  width: 24px;
`;

const StyledListItem = styled(DialogSelect.ListItem)`
  width: 100%;
  justify-content: flex-start;
  gap: 16px;
`;

const MoreButtonContainer = styled.div`
  margin-left: auto;
`;

const MoreButton = styled.button<{ $isVisible: boolean }>`
  display: flex;
  padding: 3px;
  border-radius: 50%;
  border: 1px solid transparent;
  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
  flex-shrink: 0;

  &:hover,
  :focus,
  :active,
  :focus-within {
    opacity: 1;
  }

  &:focus-within {
    border: 1px solid ${({ theme }) => theme.colorsV2.gray60};
  }

  transition: all 0.2s ease-in-out;

  > svg {
    flex-shrink: 0;
    color: ${({ theme }) => theme.colorsV2.gray70};
  }

  &:disabled {
    cursor: not-allowed;
    > svg {
      color: ${({ theme }) => theme.colorsV2.gray50};
    }
  }
`;

const TitleInputContainer = styled.div`
  padding: 16px;
  cursor: default;
`;

const StyledTrashIcon = styled(TrashIcon)`
  color: ${({ theme }) => theme.colorsV2.gray80};
  margin-left: 16px;
`;

const FooterActionLabel = styled(DialogSelect.ListItemLabel)`
  color: ${({ theme }) => theme.colorsV2.gray70};

  font-family: Inter;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  text-align: left;
`;

const AddIconContainer = styled.div`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 8px;
    border-radius: 50%;
    border: 1px solid ${theme.colorsV2.gray30};

    svg {
      display: flex;
    }
  `}
`;

const AddNewListLayout = styled.button<{ $withLessTopMargin: boolean }>`
  ${({ $withLessTopMargin, theme }) => css`
    width: 100%;
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    white-space: pre-wrap;
    border-radius: 8px;
    padding: 8px;
    margin: 8px;
    ${$withLessTopMargin
      ? css``
      : css`
          margin-top: 4px;
        `}

    div {
      flex-shrink: 0;
    }

    &:hover,
    &:focus,
    &:active,
    &:focus-within {
      background-color: ${theme.colorsV2.gray10};
    }

    transition: background-color 0.2s ease-out;
  `}
`;

const ActionListItem = styled(DialogSelect.ListItem)`
  margin: 0;
  padding: 0;
`;

const WarningMessageContainer = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 12px 12px 16px;
  color: ${({ theme }) => theme.colorsV2.gray70};
`;

const FooterActionLabelContainer = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 12px 12px 16px;
`;

const MoreActionsMenuButton = ({
  disabled,
  isVisible,
  list,
  onListDeleted,
  onListNameUpdated,
  onMenuClose,
  onMenuOpen,
}: {
  readonly disabled: boolean;
  readonly isVisible: boolean;
  readonly list: Item;
  readonly onListDeleted: (listId: string) => void;
  readonly onListNameUpdated: (listId: string, newName: string) => void;
  readonly onMenuClose: () => void;
  readonly onMenuOpen: () => void;
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const handleMenuAction = () => {
    setIsMenuOpen((previous) => !previous);

    if (isMenuOpen) {
      onMenuClose();
    } else {
      onMenuOpen();
    }
  };

  const [title, setTitle] = useState(list.name);

  if (list.isExpertList) {
    return (
      <Tooltip content="Expert lists can not be delete or renamed">
        <MoreButton
          $isVisible={isVisible}
          disabled
          tabIndex={-1}
        >
          <MoreIcon size={16} />
        </MoreButton>
      </Tooltip>
    );
  }

  if (disabled) {
    return (
      <MoreButton
        $isVisible={isVisible}
        disabled
        tabIndex={-1}
      >
        <MoreIcon size={16} />
      </MoreButton>
    );
  }

  return (
    <Menu
      isOpen={isMenuOpen}
      offset={[0, 16]}
      placement="right"
      toggleMenu={handleMenuAction}
    >
      <Menu.Reference>
        <MoreButton
          $isVisible={isVisible}
          onClick={(event) => {
            handleMenuAction();
            event.preventDefault();
            event.stopPropagation();
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              handleMenuAction();
              event.preventDefault();
              event.stopPropagation();
            }
          }}
        >
          <MoreIcon size={16} />
        </MoreButton>
      </Menu.Reference>

      <Menu.Panel forceDesktopMenuLayout>
        <TitleInputContainer
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
          }}
        >
          <TextInput
            autoComplete="off"
            autoFocus
            onBlur={() => {
              onListNameUpdated(list.id, title);
            }}
            onChange={undefined}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                onListNameUpdated(list.id, title);
                event.stopPropagation();
                event.preventDefault();
              }

              if (event.key === 'Escape') {
                setTitle(list.name);
                handleMenuAction();
                event.stopPropagation();
                event.preventDefault();
              }
            }}
            onValueChange={(value) => {
              setTitle(value ?? '');
            }}
            placeholder="List name"
            value={title}
            variant="text"
          />
        </TitleInputContainer>
        <Menu.Item
          icon={StyledTrashIcon}
          iconSize={16}
          onClick={(event) => {
            onListDeleted(list.id);
            handleMenuAction();
            event.preventDefault();
            event.stopPropagation();
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              onListDeleted(list.id);
              handleMenuAction();
              event.preventDefault();
              event.stopPropagation();
            }

            if (event.key === 'Escape') {
              setTitle(list.name);
              handleMenuAction();
              event.stopPropagation();
              event.preventDefault();
            }
          }}
        >
          Delete
        </Menu.Item>
      </Menu.Panel>
    </Menu>
  );
};

export const AddToListMenuSelect = ({
  analyticsSection,
  availableLists,
  context,
  disableSearchAndCreateInput,
  floating,
  isOpen,
  isParentMenuOpen,
  onClose,
  onListDeleted,
  onListNameUpdated,
  onNewListCreated,
  onSelectedItemChange,
  parentMenuRef,
  selectedLists,
  strategy,
  warningMessage,
  x,
  y,
}: AddToListMenuSelectProps) => {
  const { track } = useAnalytics();

  const [search, setSearch] = useState('');

  const filteredLists = useMemo(() => {
    if (!search) return availableLists;
    return availableLists.filter((list) =>
      list.name.toLowerCase().includes(search.toLowerCase()),
    );
  }, [availableLists, search]);

  const {
    activeIndex,
    getFloatingProps,
    getItemProps,
    getReferenceProps,
    listRef,
  } = useDialogSelect({ context });

  const handleSelect = useCallback(
    (newList: Item) => {
      const selectedIndex = selectedLists.findIndex(
        (list) => list.id === newList.id,
      );

      if (selectedIndex === -1) {
        onSelectedItemChange([...selectedLists, newList]);
        track('filter', {
          filter_category: 'list',
          filter_name: newList.name,
          filter_state: 'applied',
          section: analyticsSection,
        });
      } else {
        onSelectedItemChange(
          selectedLists.filter((list) => list.id !== newList.id),
        );
        track('filter', {
          filter_category: 'list',
          filter_name: newList.name,
          filter_state: 'removed',
          section: analyticsSection,
        });
      }

      setSearch('');
    },
    [analyticsSection, onSelectedItemChange, selectedLists, track],
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      const activeItem = activeIndex !== null && filteredLists[activeIndex];

      if (event.key === 'Enter') {
        event.preventDefault();
        event.stopPropagation();

        if (activeItem) {
          track('filter', {
            filter_category: 'list',
            filter_name: activeItem.id,
            filter_state: 'applied',
            section: analyticsSection,
          });

          handleSelect(activeItem);
        }
      }

      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        onClose();
      }
    },
    [
      activeIndex,
      analyticsSection,
      filteredLists,
      handleSelect,
      onClose,
      track,
    ],
  );

  const containerRef = useRef<HTMLDivElement>(null);

  const inputRef = useRef<HTMLInputElement>(null);
  const clearButtonRef = useRef<HTMLButtonElement>(null);
  const onClear: MouseEventHandler<HTMLButtonElement> = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();
      setSearch('');
      inputRef.current?.focus();

      track('button', {
        action: 'tapped',
        button_name: 'clear_filter_search_text',
        section: analyticsSection,
      });
    },
    [analyticsSection, track],
  );

  const handleInputOnChange: (event: ChangeEvent<HTMLInputElement>) => void = (
    event,
  ) => {
    event.preventDefault();
    event.stopPropagation();
    setSearch(event.target.value);
  };

  const isBelowSmallScreen = useIsSmallScreen();

  const [isSubMenuOpen, setIsSubMenuOpen] = useState(false);

  const onSubMenuOpen = useCallback((open: boolean) => {
    setIsSubMenuOpen(open);
  }, []);

  useClickAway(containerRef, (event) => {
    if (
      isBelowSmallScreen ||
      isSubMenuOpen ||
      isParentMenuOpen ||
      (event.target instanceof Element &&
        parentMenuRef?.current &&
        !parentMenuRef.current.contains(event.target))
    ) {
      return;
    }

    if (isOpen) {
      onClose();
    }
  });

  return (
    <div ref={containerRef}>
      {isOpen ? (
        <DialogSelect
          $strategy={strategy}
          $x={x}
          $y={y}
          aria-label="Add to list menu"
          onClick={(event) => {
            event.stopPropagation();
          }}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              event.preventDefault();
              event.stopPropagation();
              onClose();
            }
          }}
          ref={floating}
        >
          <DialogSelect.InputGroup
            onClose={onClose}
            withDoneText
          >
            {search.length > 0 && !isBelowSmallScreen ? (
              <ClearSearchButton
                onClick={onClear}
                ref={clearButtonRef}
                type="button"
              >
                <CloseIcon size={16} />
              </ClearSearchButton>
            ) : null}
            <DialogSelect.Input
              {...getReferenceProps({
                'aria-autocomplete': 'list',
                autoFocus: true,
                disabled: disableSearchAndCreateInput,
                onChange: handleInputOnChange,
                onClick: (event) => {
                  event.preventDefault();
                  event.stopPropagation();
                },
                onKeyDown: handleKeyDown,
                placeholder: 'Search or create a new list',
                ref: inputRef,
                tabIndex: -1,
                value: search,
              })}
            />
          </DialogSelect.InputGroup>

          <DialogSelect.ListContainer $maxHeight={240}>
            {warningMessage ? (
              <WarningMessageContainer>
                <Text
                  as="span"
                  textStyle="text-xs-regular"
                >
                  {warningMessage}
                </Text>
              </WarningMessageContainer>
            ) : null}
            <DialogSelect.List
              aria-label="List Options"
              {...getFloatingProps()}
            >
              {filteredLists.map((list, index) => {
                const isSelected = selectedLists.some(
                  (item) => item.id === list.id,
                );
                const isHighlighted = index === activeIndex;
                return (
                  <StyledListItem
                    isHighlighted={isHighlighted}
                    key={list.id}
                    {...getItemProps({
                      'aria-labelledby': `${list.id}:label`,
                      'aria-selected': isSelected,
                      id: list.id,
                      onClick: (event) => {
                        handleSelect(list);
                        event.preventDefault();
                        event.stopPropagation();
                      },
                      onKeyDown: (event) => {
                        if (event.key === 'Enter') {
                          handleSelect(list);

                          event.preventDefault();
                          event.stopPropagation();
                        }
                      },
                      ref: (node) => {
                        listRef.current[index] = node;
                      },
                      role: 'option',
                    })}
                  >
                    <CheckboxContainer>
                      <Checkbox
                        $size="small"
                        checked={isSelected}
                        defaultChecked={isSelected}
                      />
                    </CheckboxContainer>
                    <DialogSelect.ListItemLabel id={`${list.id}:label`}>
                      {list.name}
                      {list.isExpertList ? <VerifiedExpertListBadge /> : null}
                    </DialogSelect.ListItemLabel>
                    <MoreButtonContainer>
                      <MoreActionsMenuButton
                        disabled={disableSearchAndCreateInput ?? false}
                        isVisible={isHighlighted || isBelowSmallScreen}
                        list={list}
                        onListDeleted={onListDeleted}
                        onListNameUpdated={onListNameUpdated}
                        onMenuClose={() => onSubMenuOpen(false)}
                        onMenuOpen={() => onSubMenuOpen(true)}
                      />
                    </MoreButtonContainer>
                  </StyledListItem>
                );
              })}

              {availableLists.length === 0 && search.length === 0 ? (
                <FooterActionLabelContainer
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                  }}
                >
                  <FooterActionLabel>No lists created yet</FooterActionLabel>
                </FooterActionLabelContainer>
              ) : null}

              {search.length > 0 &&
              !availableLists.some(
                (list) => list.name.toLowerCase() === search.toLowerCase(),
              ) ? (
                <ActionListItem
                  isHighlighted={false}
                  onClick={(event) => {
                    onNewListCreated(search);
                    setSearch('');

                    event.preventDefault();
                    event.stopPropagation();
                  }}
                >
                  <AddNewListLayout
                    $withLessTopMargin={filteredLists.length === 0}
                  >
                    <AddIconContainer>
                      <AddIcon size={16} />
                    </AddIconContainer>
                    <Text textStyle="bodySmall">{`Add "${search}" list`}</Text>
                  </AddNewListLayout>
                </ActionListItem>
              ) : null}
            </DialogSelect.List>
          </DialogSelect.ListContainer>
        </DialogSelect>
      ) : null}
    </div>
  );
};
